import { DOCUMENT } from '@angular/common';
import { catchError, map, Observable, throwError as observableThrowError } from 'rxjs';
import { finalize, shareReplay, switchMap } from 'rxjs/operators';

import { HttpEvent, HttpEventType, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { appConfig } from 'environments/environment';
import { Credentials } from 'app/models/user.model';
import { AuthService } from './auth.service';
import { AppStore } from 'app/store/app.store';

let localtimeOffset = 0;
let activeRequestCount = 0;
let isCursorWaiting: boolean | null = null;
let refreshRequest$: Observable<Credentials> | null = null;

export const apiInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const store = inject(AppStore);
  const document = inject(DOCUMENT);

  if (request.url.includes('i18n') || request.url.includes('mat-icons')) {
    return next(request);
  }

  if (authService.noAuthEndPoint.includes(request.url)) {
    return handleRequest(next, request.clone({ url: `${appConfig.apiHost}${request.url}` }), store, document, authService);
  }

  const credentials = store.credentials?.();
  const encodedUri = encodeURI(request.url).replace(/%5B/g, '[').replace(/%5D/g, ']').replace('+', '%2B');

  return handleRequest(
    next,
    request.clone({
      url: appConfig.apiHost + encodedUri,
      setHeaders: credentials?.token
        ? {
            Authorization: `Bearer ${credentials.token}`,
          }
        : {},
    }),
    store,
    document,
    authService,
  );
};

function handleRequest(
  next: HttpHandlerFn,
  request: HttpRequest<unknown>,
  store: InstanceType<typeof AppStore>,
  document: Document,
  authService: AuthService,
): Observable<HttpEvent<unknown>> {
  const shouldTrackCursor = !isBackgroundRequest(request.url);

  return next(request).pipe(
    map((event: HttpEvent<unknown>) => {
      if (shouldTrackCursor && event.type === HttpEventType.Sent) {
        activeRequestCount++;
        changeCursor(document);
      }

      if (event instanceof HttpResponse) {
        if (shouldTrackCursor) {
          activeRequestCount--;
          changeCursor(document);
        }

        if (localtimeOffset === 0 && event.body && typeof event.body === 'object' && 'generatedAt' in event.body) {
          localtimeOffset = new Date().getTime() - Number(event.body.generatedAt);
        }

        return event.clone({
          body: {
            data: event.body,
            statusCode: event.status,
          },
        });
      }

      return event;
    }),
    catchError(err => {
      if (shouldTrackCursor) {
        activeRequestCount = Math.max(activeRequestCount - 1, 0);
        changeCursor(document);
      }

      const isRefreshRequest = request.url.includes('/api/auth/refresh-token');
      const alreadyRetried = request.headers.has('X-Refresh-Retry');
      const refreshToken = store.credentials?.()?.refreshToken;

      if (err.status === 401 && !isRefreshRequest && !alreadyRetried && refreshToken) {
        if (!refreshRequest$) {
          refreshRequest$ = authService.refreshToken(refreshToken).pipe(
            map(refreshedCredentials => {
              authService.setCredentials(refreshedCredentials);
              store.setCredentials(refreshedCredentials);

              return refreshedCredentials;
            }),
            catchError(refreshErr => {
              store.logout();

              return observableThrowError(() => normalizeApiError(refreshErr, 401, 'Token refresh failed'));
            }),
            finalize(() => {
              refreshRequest$ = null;
            }),
            shareReplay(1),
          );
        }

        return refreshRequest$.pipe(
          switchMap(refreshedCredentials => {
            const retriedRequest = request.clone({
              setHeaders: {
                Authorization: `Bearer ${refreshedCredentials.token}`,
                'X-Refresh-Retry': '1',
              },
            });

            return handleRequest(next, retriedRequest, store, document, authService);
          }),
          catchError(refreshErr => {
            return observableThrowError(() => normalizeApiError(refreshErr, 401, 'Token refresh failed'));
          }),
        );
      }

      if (err.status === 401) {
        store.logout();
      }

      return observableThrowError(() => ({ statusCode: err.status, data: {}, message: err.message, error: err.error }));
    }),
  );
}

function normalizeApiError(error: unknown, fallbackStatusCode: number, fallbackMessage: string): unknown {
  if (typeof error !== 'object' || error === null) {
    return { statusCode: fallbackStatusCode, data: {}, message: fallbackMessage, error: undefined };
  }

  if ('statusCode' in error && typeof error.statusCode === 'number') {
    return error;
  }

  return {
    statusCode: 'status' in error && typeof error.status === 'number' ? error.status : fallbackStatusCode,
    data: {},
    message: 'message' in error && typeof error.message === 'string' ? error.message : fallbackMessage,
    error: 'error' in error ? error.error : undefined,
  };
}

function isBackgroundRequest(url: string): boolean {
  return url.includes('/status');
}

function changeCursor(document: Document): void {
  const body = document.body;
  if (!body) {
    return;
  }

  const shouldWait = activeRequestCount > 0;

  if (isCursorWaiting === shouldWait) {
    return;
  }

  isCursorWaiting = shouldWait;
  body.style.cursor = shouldWait ? 'wait' : 'initial';
}
